import { toApiStatus, toUiStatus } from './_helpers';

function userTypeLabel(value) {
  if (value === 'agency') return 'Buyer';
  if (value === 'service_provider') return 'Seller';
  return toUiStatus(value) || '—';
}

function registrationLabel(value) {
  if (!value) return '—';
  const map = {
    otp_verified: 'OTP verified',
    basic_done: 'Basic done',
    aadhaar_done: 'Documents done',
    complete: 'Complete',
  };
  return map[value] || toUiStatus(value);
}

export function appUserFromApi(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    userType: raw.userType || 'service_provider',
    userTypeLabel: userTypeLabel(raw.userType),
    firstName: raw.firstName || '',
    middleName: raw.middleName || '',
    lastName: raw.lastName || '',
    fullName: raw.fullName || '',
    ownerName: raw.ownerName || '',
    tradeName: raw.tradeName || '',
    gstNumber: raw.gstNumber || '',
    constitution: raw.constitution || '',
    gstDetails: raw.gstDetails || null,
    roles: raw.roles || null,
    phone: raw.phone || '',
    email: raw.email || '',
    village: raw.village || '',
    taluka: raw.taluka || '',
    district: raw.district || '',
    state: raw.state || '',
    pin: raw.pin || '',
    address1: raw.address1 || '',
    address2: raw.address2 || '',
    gender: raw.gender || '',
    dob: raw.dob || null,
    education: raw.education || '',
    educationDetails: raw.educationDetails || '',
    secondaryContact: raw.secondaryContact || '',
    profilePhotoUrl: raw.profilePhotoUrl || '',
    registrationStatus: raw.registrationStatus || '',
    registrationStatusLabel: registrationLabel(raw.registrationStatus),
    verificationStatus: toUiStatus(raw.verificationStatus) || 'Pending',
    rejectionReason: raw.rejectionReason || '',
    isActive: raw.isActive !== false,
    offerings: Array.isArray(raw.offerings) ? raw.offerings : [],
    specialServices: Array.isArray(raw.specialServices) ? raw.specialServices : [],
    documents: Array.isArray(raw.documents) ? raw.documents : [],
    materials: Array.isArray(raw.materials) ? raw.materials : [],
    equipments: Array.isArray(raw.equipments) ? raw.equipments : [],
    businessUnits: Array.isArray(raw.businessUnits)
      ? raw.businessUnits.map((a) => ({
          id: a.id,
          buId: a.buId,
          siteId: a.siteId ?? a.site?.id ?? null,
          isPrimary: Boolean(a.isPrimary),
          assignedAt: a.assignedAt || null,
          shortName: a.businessUnit?.shortName || a.shortName || '',
          fullNameGst: a.businessUnit?.fullNameGst || a.fullNameGst || '',
          siteName: a.site?.siteName || a.siteName || '',
        }))
      : [],
    primaryBuId: raw.primaryBuId ?? null,
    primaryBuName: raw.primaryBuName || '',
    primarySiteName: raw.primarySiteName || '',
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
    lastLoginAt: raw.lastLoginAt || null,
    lastOtpCode: raw.lastOtpCode || null,
    lastOtpSentAt: raw.lastOtpSentAt || null,
    lastOtpExpiresAt: raw.lastOtpExpiresAt || null,
    lastOtpVerified: raw.lastOtpVerified ?? null,
  };
}

export function appUserKycToApi(ui) {
  return {
    verificationStatus: toApiStatus(ui.verificationStatus),
    rejectionReason:
      ui.verificationStatus === 'Rejected' || ui.verificationStatus === 'rejected'
        ? ui.rejectionReason || null
        : null,
    role: ui.role || undefined,
    ...(ui.employeeCode !== undefined
      ? { employeeCode: ui.employeeCode == null ? '' : String(ui.employeeCode) }
      : {}),
    ...(ui.notificationTitle != null
      ? { notificationTitle: String(ui.notificationTitle).trim() || null }
      : {}),
    ...(ui.notificationBody != null
      ? { notificationBody: String(ui.notificationBody).trim() || null }
      : {}),
  };
}

/** Default member notification for role + status (keep in sync with backend). */
export function buildVerificationNotifyDefaults({
  role,
  status,
  rejectionReason = '',
}) {
  const isBuyer = role === 'agency';
  const roleTitle = isBuyer ? 'Buyer' : 'Seller';
  const apiStatus = String(status || '').toLowerCase();

  if (apiStatus === 'verified') {
    return {
      title: `${roleTitle} account verified`,
      body: isBuyer
        ? 'Your buyer account is verified now. You can raise equipment and material requests.'
        : 'Your seller account is verified now. You can be matched and recommended for work.',
    };
  }
  if (apiStatus === 'rejected') {
    const reason = String(rejectionReason || '').trim();
    return {
      title: `${roleTitle} verification update`,
      body: reason
        ? `Your ${isBuyer ? 'buyer' : 'seller'} verification needs an update: ${reason}`
        : isBuyer
          ? 'Your buyer verification needs an update. Please review your documents and profile details, then try again.'
          : 'Your seller verification needs an update. Please review your documents and profile details, then try again.',
    };
  }
  if (apiStatus === 'pending') {
    return {
      title: `${roleTitle} verification pending`,
      body: isBuyer
        ? 'Your buyer account is under review. You will be notified once buyer verification is complete.'
        : 'Your seller account is under review. You will be notified once seller verification is complete.',
    };
  }
  return {
    title: `${roleTitle} verification update`,
    body: `Your ${isBuyer ? 'buyer' : 'seller'} account verification status was updated.`,
  };
}
